import type { Connector, ToolConnector } from './types';
import { CALLER_LANGUAGE } from './voice-agent';

// Per-hub "speak replies" policy, shared by every TTS connector. Gates whether a
// voice-message turn (`userSentAudio`) gets a synthesized spoken reply — and the
// associated TTS billing. Read at TTS-generation time in ConversationDO (both the
// terminal reply and the mid-turn preamble) AND'd with a channel-can-carry-audio
// capability check, so audio is never synthesized-then-discarded (the #3152/#3173
// class). Default ON preserves the pre-setting behavior; only an explicit `false`
// opts out. `getConnectionSettings` returns raw stored settings with no schema
// merge, so the ON default is applied in code (`voice_reply_enabled !== false`).
const TTS_VOICE_REPLY_ENABLED_FIELD = {
  voice_reply_enabled: {
    type: 'toggle' as const,
    label: 'Speak replies to voice messages',
    default: true,
    description:
      'When a user sends a voice message, reply with synthesized audio on channels that support it (WhatsApp, Instagram, Telegram, and the app). Turn this off to always reply in text — no audio is generated and no TTS usage is billed. Replies to text messages are always text.',
  },
};

// Copilot suggestion trigger (consultant-agents H1.1 — copilot on-demand). Consulted
// ONLY for an agent running on the COPILOT track. `every_message` (default) drafts a
// suggestion on every inbound — today's behavior; `on_demand` suppresses the automatic
// suggestion at the `shouldRespond` seam so the support team pulls one explicitly with
// the "Suggest reply" button. Both a cost lever (copilot bills per fired suggestion) and
// a noise lever. Validated generically by `validateAgentSettings`/`sanitizeAgentSettings`
// (no special-casing). No effect on Pilot or background agents. MUST be spread into EVERY
// `Agent` connector carrying `agent_settings_schema` — omitting it on one connector means
// `sanitizeAgentSettings` silently strips the field for agents on that connector. The
// `Realtime` connector carries none: its agent is the voice layer, which never runs on the
// copilot track, so the field could only be stored and ignored.
const COPILOT_TRIGGER_FIELD = {
  copilot_trigger: {
    type: 'select',
    label: 'Copilot Suggestions',
    options: [
      { value: 'every_message', label: 'Every message (automatic)' },
      { value: 'on_demand', label: 'On demand (Suggest reply button)' },
    ],
    default: 'every_message',
    description: 'When this agent runs as a Copilot, whether it drafts a suggestion automatically on every incoming message ("Every message") or only when a team member clicks "Suggest reply" ("On demand"). On demand reduces cost and noise. No effect on Pilot agents.',
  },
};

// WayAI's code sandbox for native agents (wayai#5083 Phase 3): a disposable container, the
// conversation's (shared by its agents whose `sandbox_scope` is `conversation`) or one agent's own
// in that conversation (`agent`, decision 147), started by the first sandbox tool call. MUST be
// spread into EVERY native `Agent` connector, like `COPILOT_TRIGGER_FIELD`: a connector without it would have
// `sanitizeAgentSettings` strip the fields, and its agents could never run code. The network
// setting's three modes and their descriptions are the honest labels of the sandbox's egress
// policy (`apps/backend/src/sandbox/egress-policy.ts`); `sandbox_allowed_hosts` holds at most
// that policy's host limit, which a backend test pins.
const SANDBOX_AGENT_FIELDS = {
  code_execution_enabled: {
    type: 'toggle' as const,
    label: 'Code Execution Sandbox',
    default: false,
    description: 'Let this agent run code in a WayAI sandbox with the sandbox tools, bash, copy_to_sandbox and copy_from_sandbox (add them under Tools): a disposable Linux container, shared with the conversation\'s other agents or private to this agent (Sandbox Scope), started on first use and ended after 10 minutes without use, 60 minutes in all, or when the conversation closes.',
  },
  sandbox_scope: {
    type: 'select' as const,
    label: 'Sandbox Scope',
    options: [
      { value: 'conversation', label: 'Conversation (shared with the conversation\'s other agents)' },
      { value: 'agent', label: 'Agent (private to this agent)' },
    ],
    default: 'conversation',
    description: 'Whose sandbox this agent uses. Conversation: shared with the conversation\'s other agents; every agent sharing it sees its files, and agents sharing it need the same Sandbox Network setting, or a command from one ends the sandbox and starts an empty one. Agent: private to this agent; no other agent sees its files. Set Agent for an agent that handles files others must not see, such as a copilot with internal documents. Each agent sandbox is its own container, counts toward the organization\'s limit on running sandboxes, and ends with the conversation.',
  },
  sandbox_network: {
    type: 'select' as const,
    label: 'Sandbox Network',
    options: [
      { value: 'none', label: 'No network' },
      { value: 'allowlist', label: 'Allowlist' },
      { value: 'web', label: 'Web' },
    ],
    default: 'none',
    description: 'What code in this agent\'s sandbox can reach. No network: nothing leaves the sandbox. Allowlist: HTTP and HTTPS to the hosts listed below only, through WayAI; no DNS or other protocol leaves, but data can still be sent to those hosts. Web: HTTP and HTTPS to any public host, through WayAI; data can be sent to any of them. WayAI adds no credentials to these requests.',
  },
  sandbox_allowed_hosts: {
    type: 'array' as const,
    itemType: 'text' as const,
    maxItems: 50,
    label: 'Sandbox Allowed Hosts',
    description: 'The exact host names the sandbox may reach under Allowlist, such as api.example.com: no wildcards, IP addresses, ports or paths.',
    dependsOn: { field: 'sandbox_network', values: ['allowlist'] },
  },
};

// Empty string = auto-detect (STT) or model-default (TTS).
const AUDIO_LANGUAGE_OPTIONS = [
  { value: '', label: 'Auto-detect' },
  { value: 'en', label: 'en' },
  { value: 'es', label: 'es' },
  { value: 'pt', label: 'pt' },
  { value: 'fr', label: 'fr' },
  { value: 'de', label: 'de' },
  { value: 'it', label: 'it' },
  { value: 'nl', label: 'nl' },
  { value: 'ja', label: 'ja' },
  { value: 'ko', label: 'ko' },
  { value: 'zh', label: 'zh' },
  { value: 'ar', label: 'ar' },
  { value: 'ru', label: 'ru' },
  { value: 'hi', label: 'hi' },
  { value: 'tr', label: 'tr' },
];

// ---------------------------------------------------------------------------
// Channel Connectors
// ---------------------------------------------------------------------------

const whatsapp: Connector = {
  connector_id: '5fb214cb-aaa8-4b3d-8c65-c9370b3e7c85',
  service_name: 'Whatsapp',
  connector_type: 'Channel',
  connector_name: 'WhatsApp',
  authentication_types: ['oauth'],
  init_endpoint: 'whatsapp_embedded_signup',
  unique_per_hub: false,
  account_sid_placeholder: 'Enter WhatsApp Business phone number',
  models: null,
  is_internal: true,
  is_harness: false,
  connector_module: 'channel',
  enabled: true,
  connector_settings_schema: {
    voice_reply_as_voice_note: { type: 'toggle', label: 'Send voice replies as voice notes', default: false, description: 'Deliver TTS audio replies as native WhatsApp voice notes (push-to-talk UI + WhatsApp’s on-device transcript) instead of a basic audio attachment. Falls back to a basic audio attachment if the voice-note conversion fails.' },
    voice_reply_send_transcript: { type: 'toggle', label: 'Send transcript with voice replies', default: true, description: 'Also send the reply text after the audio. When off, the text is skipped only when the audio actually shipped as a voice note (WhatsApp shows its own transcript); a basic-audio fallback always includes the text.' },
  },
  agent_settings_schema: null,
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: null,
  token_refresh_config: { oauth: { strategy: 'token_exchange', access_token: { refresh_window_hours: 168, default_lifetime_seconds: 5184000 } } },
  connector_description: 'Send and receive WhatsApp messages via Meta Business API with embedded signup.',
};

const instagram: Connector = {
  connector_id: 'f9e8d7c6-5b4a-3210-9876-543210fedcba',
  service_name: 'Instagram',
  connector_type: 'Channel',
  connector_name: 'Instagram',
  authentication_types: ['oauth'],
  init_endpoint: 'instagram_callback',
  unique_per_hub: false,
  account_sid_placeholder: 'Enter Instagram Business account username',
  models: null,
  is_internal: true,
  is_harness: false,
  connector_module: 'channel',
  enabled: true,
  connector_settings_schema: null,
  agent_settings_schema: null,
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: null,
  token_refresh_config: { oauth: { strategy: 'token_exchange', access_token: { refresh_window_hours: 168, default_lifetime_seconds: 5184000 } } },
  connector_description: 'Send and receive Instagram Direct Messages via Meta Business API.',
};

const resend: Connector = {
  connector_id: 'a1b2c3d4-e5f6-4a89-b012-3e5e0d000001',
  service_name: 'Resend',
  connector_type: 'Channel',
  connector_name: 'Resend',
  authentication_types: ['api_key'],
  init_endpoint: null,
  unique_per_hub: false,
  account_sid_placeholder: 'Enter your from email address (e.g. support@yourdomain.com)',
  models: null,
  is_internal: true,
  is_harness: false,
  connector_module: 'channel',
  enabled: true,
  connector_settings_schema: null,
  agent_settings_schema: null,
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: {
    api_key: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'e.g. Support Email', required: true },
      api_key: { type: 'password', label: 'Resend API Key', placeholder: 're_xxxxxxxxxx', description: 'From resend.com/api-keys', required: true, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_API_KEY__' },
    },
  },
  token_refresh_config: { api_key: { strategy: 'none' } },
  connector_description: 'Send and receive emails via Resend API with email forwarding.',
};

const telegram: Connector = {
  connector_id: 'a1b2c3d4-e5f6-4a89-b012-3e5e0d000002',
  service_name: 'Telegram',
  connector_type: 'Channel',
  connector_name: 'Telegram',
  authentication_types: ['api_key'],
  init_endpoint: null,
  unique_per_hub: false,
  account_sid_placeholder: 'Enter Telegram bot username (e.g. @my_bot)',
  models: null,
  is_internal: true,
  is_harness: false,
  connector_module: 'channel',
  enabled: true,
  connector_settings_schema: null,
  agent_settings_schema: null,
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: {
    api_key: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'e.g. Support Bot', required: true },
      api_key: { type: 'password', label: 'Bot Token', placeholder: 'Paste token from @BotFather', description: 'Open Telegram, message @BotFather, send /newbot, and paste the token here', required: true, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_API_KEY__' },
    },
  },
  token_refresh_config: { api_key: { strategy: 'none' } },
  connector_description: 'Send and receive Telegram messages via Bot API.',
};

// The API channel: machine-to-machine conversational access to a hub. An external
// system POSTs messages over REST (bearer `way_` token, hub-admin grade) and the
// hub's replies are delivered back as signed webhooks — no messaging provider, no
// LLM on the caller's side. Unlike provider channels there is no external address
// (phone/email/sid), so `account_sid_placeholder` is null. `webhook_signing_secret`
// is the outbound HMAC key (server-generated when omitted); `webhook_url` +
// `delivery_events` are non-secret settings. Enabled after the dark-launch series
// (schema/credentials, settings validation, inbound route, outbound webhooks, UI) landed;
// reverting `enabled` to false hides it from the Add list and rejects new connections
// (existing api channels keep working — the per-hub off-switch is disabling the channel).
// Single source for the delivery-event set so `options` and the "all events" `default`
// can't drift — adding an event here updates both.
const API_CHANNEL_DELIVERY_EVENTS = ['message.created', 'conversation.status_changed', 'conversation.ended'] as const;

const apiChannel: Connector = {
  connector_id: 'a1b2c3d4-e5f6-4a89-b012-3e5e0d000003',
  service_name: 'ApiChannel',
  connector_type: 'Channel',
  connector_name: 'API Channel',
  authentication_types: ['api_key'],
  init_endpoint: null,
  unique_per_hub: false,
  account_sid_placeholder: null,
  models: null,
  is_internal: true,
  is_harness: false,
  connector_module: 'channel',
  enabled: true,
  connector_settings_schema: {
    // `format: 'url'` → lexical https check in validate/sanitizeAgentSettings + the
    // SSRF blocklist (validateWebhookUrl) at the backend connection-write seams.
    webhook_url: { type: 'text', format: 'url', label: 'Webhook URL', placeholder: 'https://example.com/wayai/webhook', description: 'HTTPS endpoint that receives signed webhook deliveries for this integration.', required: true },
    delivery_events: {
      type: 'array',
      itemType: 'select',
      label: 'Delivery events',
      description: 'Which conversation events are delivered to the webhook. Defaults to all.',
      options: [...API_CHANNEL_DELIVERY_EVENTS],
      default: [...API_CHANNEL_DELIVERY_EVENTS],
    },
  },
  agent_settings_schema: null,
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: {
    api_key: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'e.g. Orders Bridge', required: true },
      webhook_signing_secret: { type: 'password', label: 'Webhook Signing Secret', description: 'HMAC-SHA256 key used to sign outbound webhook deliveries. Leave blank to auto-generate a strong secret.', required: false, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_WEBHOOK_SIGNING_SECRET__' },
    },
  },
  token_refresh_config: { api_key: { strategy: 'none' } },
  connector_description: 'Start and continue hub conversations over REST; receive replies as signed webhooks.',
};

// ---------------------------------------------------------------------------
// LLM Providers (Agent)
// ---------------------------------------------------------------------------

// OpenAI models that take reasoning effort, verbosity and service tier — must match the
// backend adapter's REASONING_MODELS (apps/backend/src/llm/providers/openai.ts).
const OPENAI_REASONING_MODELS = ['gpt-6.1-sol', 'gpt-6-sol', 'gpt-6-astra', 'gpt-6-luna', 'gpt-5.6-sol', 'gpt-5.6-terra', 'gpt-5.6-luna', 'gpt-5.5', 'gpt-5.4', 'gpt-5.4-mini', 'gpt-5.4-nano', 'gpt-5.2', 'gpt-5.1', 'gpt-5', 'gpt-5-mini', 'gpt-5-nano'];

const openai: Connector = {
  connector_id: '0cd6a292-895b-4667-b89e-dd298628c272',
  service_name: 'Openai',
  connector_type: 'Agent',
  connector_name: 'OpenAI',
  authentication_types: ['api_key'],
  init_endpoint: null,
  unique_per_hub: true,
  account_sid_placeholder: 'Enter email of the account',
  models: ['gpt-6.1-sol', 'gpt-6-astra', 'gpt-6-luna', 'gpt-6-sol', 'gpt-5.6-sol', 'gpt-5.6-terra', 'gpt-5.6-luna', 'gpt-5.5', 'gpt-5.4-mini', 'gpt-5.4-nano', 'gpt-5.4', 'gpt-5.2', 'gpt-5.2-chat-latest', 'gpt-5.1', 'gpt-5', 'gpt-5-mini', 'gpt-5-nano', 'gpt-4.1', 'gpt-4.1-mini'],
  is_internal: true,
  is_harness: false,
  connector_module: 'agent',
  enabled: true,
  connector_settings_schema: null,
  agent_settings_schema: {
    model: { type: 'select', label: 'Model', allowCustomValue: true, options: [{ value: 'gpt-6.1-sol', label: 'GPT-6.1 Sol', deprecated: false }, { value: 'gpt-6-astra', label: 'GPT-6 Astra', deprecated: false }, { value: 'gpt-6-luna', label: 'GPT-6 Luna', deprecated: false }, { value: 'gpt-6-sol', label: 'GPT-6 Sol (Legacy)', deprecated: true }, { value: 'gpt-5.6-sol', label: 'GPT-5.6 Sol (Legacy)', deprecated: true }, { value: 'gpt-5.6-terra', label: 'GPT-5.6 Terra (Legacy)', deprecated: true }, { value: 'gpt-5.6-luna', label: 'GPT-5.6 Luna (Legacy)', deprecated: true }, { value: 'gpt-5.5', label: 'GPT-5.5 (Legacy)', deprecated: true }, { value: 'gpt-5.4-mini', label: 'GPT-5.4 Mini (Legacy)', deprecated: true }, { value: 'gpt-5.4-nano', label: 'GPT-5.4 Nano (Legacy)', deprecated: true }, { value: 'gpt-5.4', label: 'GPT-5.4 (Legacy)', deprecated: true }, { value: 'gpt-5.2', label: 'GPT-5.2 (Legacy)', deprecated: true }, { value: 'gpt-5.2-chat-latest', label: 'GPT-5.2 Chat (Legacy)', deprecated: true }, { value: 'gpt-5.1', label: 'GPT-5.1 (Legacy)', deprecated: true }, { value: 'gpt-5', label: 'GPT-5 (Legacy)', deprecated: true }, { value: 'gpt-5-mini', label: 'GPT-5 Mini (Legacy)', deprecated: true }, { value: 'gpt-5-nano', label: 'GPT-5 Nano (Legacy)', deprecated: true }, { value: 'gpt-4.1', label: 'GPT-4.1 (Legacy)', deprecated: true }, { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini (Legacy)', deprecated: true }], default: 'gpt-6.1-sol', description: 'AI model to use', required: true },
    temperature: { type: 'slider', label: 'Temperature', min: 0, max: 2, default: 0.7, step: 0.01, description: 'Controls randomness (0=focused, 2=creative). Only for non-reasoning models.', dependsOn: { field: 'model', values: ['gpt-5.2-chat-latest', 'gpt-4.1-mini', 'gpt-4.1'] } },
    top_p: { type: 'slider', label: 'Top P', min: 0, max: 1, default: 1, step: 0.01, description: 'Nucleus sampling parameter. Only for non-reasoning models.', dependsOn: { field: 'model', values: ['gpt-5.2-chat-latest', 'gpt-4.1-mini', 'gpt-4.1'] } },
    reasoning_effort: { type: 'select', label: 'Reasoning Effort', options: [{ value: 'minimal', label: 'minimal', deprecated: true }, 'low', 'medium', 'high', 'xhigh', 'max', 'none'], default: 'medium', description: 'How much effort the model spends reasoning. Higher effort = more tokens & time. Set to none for fastest responses. xhigh requires gpt-5.2 or newer; max requires gpt-5.6 or newer; on older models each is clamped down to the highest supported level. GPT-6 Astra and GPT-6.1 Sol have no none level — none runs them at low. minimal is a legacy level only gpt-5 / gpt-5.1 accept — it is clamped to low on gpt-5.2 and newer.', dependsOn: { field: 'model', values: OPENAI_REASONING_MODELS } },
    verbosity: { type: 'select', label: 'Verbosity', options: ['low', 'medium', 'high'], default: 'medium', description: 'Output detail level. Only for reasoning models.', dependsOn: { field: 'model', values: OPENAI_REASONING_MODELS } },
    service_tier: { type: 'select', label: 'Service Tier', options: ['auto', 'flex', 'default', 'priority'], default: 'auto', description: 'Service tier (flex=50% cheaper, higher latency). Only for reasoning models.', dependsOn: { field: 'model', values: OPENAI_REASONING_MODELS } },
    max_tokens: { type: 'number', label: 'Max Tokens', min: 1, default: 4096, description: 'Maximum tokens to generate. On reasoning models, this cap includes reasoning tokens.' },
    ...SANDBOX_AGENT_FIELDS,
    streaming_enabled: { type: 'toggle', label: 'Stream Responses', default: false, description: 'Stream responses in real-time (app channel only)' },
    deliver_preamble: { type: 'toggle', label: 'Deliver Pre-Tool Messages', default: true, description: 'Send the short message the model writes before running tools ("Let me check that…") as its own message, so the user hears something during the tool-execution wait. Off = only the final reply is delivered (pre-tool text is discarded). Not applied on email channels; on voice conversations each preamble is spoken as its own audio clip.' },
    file_handling_mode: { type: 'select', label: 'File handling', options: [{ value: 'always_attach', label: 'Attach files' }, { value: 'metadata_only', label: 'Metadata only (retrieve on demand)' }], default: 'always_attach', description: 'No longer changes what the model receives — a conversation\'s earlier files are always announced by path, never re-sent as content. metadata_only still auto-enables read_file so the agent can open one of those paths on demand.' },
    max_attachment_size_mb: { type: 'number', label: 'Max attachment size (MB)', min: 1, description: 'No longer has any effect: earlier files are announced by path rather than attached, so there is no attachment size to cap.' },
    ...COPILOT_TRIGGER_FIELD,
  },
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: {
    api_key: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'Enter a name for this connection', description: 'This name will be used to identify the connection in the system', required: true },
      api_key: { type: 'password', label: 'API Key', placeholder: 'Enter your OpenAI API key', description: 'Your OpenAI API key for chat completions', required: true, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_API_KEY__' },
    },
  },
  token_refresh_config: { api_key: { strategy: 'none' } },
  connector_description: 'LLM provider for OpenAI GPT models. Requires API key.',
};

const anthropic: Connector = {
  connector_id: 'b3c4d5e6-f7a8-9012-bcde-f12345678902',
  service_name: 'Anthropic',
  connector_type: 'Agent',
  connector_name: 'Anthropic',
  authentication_types: ['api_key'],
  init_endpoint: null,
  unique_per_hub: true,
  account_sid_placeholder: 'Enter email of the account',
  models: ['claude-fable-5-1', 'claude-opus-5-5', 'claude-sonnet-5-5', 'claude-haiku-5-5', 'claude-fable-5', 'claude-sonnet-5', 'claude-haiku-4-5', 'claude-opus-4-8', 'claude-opus-4-7', 'claude-sonnet-4-6', 'claude-sonnet-4-5', 'claude-opus-4-6', 'claude-opus-4-5'],
  is_internal: true,
  is_harness: false,
  connector_module: 'agent',
  enabled: true,
  connector_settings_schema: null,
  agent_settings_schema: {
    model: { type: 'select', label: 'Model', allowCustomValue: true, options: [{ value: 'claude-fable-5-1', label: 'Claude Fable 5.1', deprecated: false }, { value: 'claude-opus-5-5', label: 'Claude Opus 5.5', deprecated: false }, { value: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5', deprecated: false }, { value: 'claude-haiku-5-5', label: 'Claude Haiku 5.5', deprecated: false }, { value: 'claude-fable-5', label: 'Claude Fable 5 (Legacy)', deprecated: true }, { value: 'claude-sonnet-5', label: 'Claude Sonnet 5 (Legacy)', deprecated: true }, { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5 (Legacy)', deprecated: true }, { value: 'claude-opus-4-8', label: 'Claude Opus 4.8 (Legacy)', deprecated: true }, { value: 'claude-opus-4-7', label: 'Claude Opus 4.7 (Legacy)', deprecated: true }, { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (Legacy)', deprecated: true }, { value: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5 (Legacy)', deprecated: true }, { value: 'claude-opus-4-6', label: 'Claude Opus 4.6 (Legacy)', deprecated: true }, { value: 'claude-opus-4-5', label: 'Claude Opus 4.5 (Legacy)', deprecated: true }], default: 'claude-sonnet-5-5', description: 'Claude model to use', required: true },
    temperature: { type: 'slider', label: 'Temperature', min: 0, max: 1, default: 1, step: 0.01, description: 'Controls randomness (0=focused, 1=creative)' },
    max_tokens: { type: 'number', label: 'Max Tokens', min: 1, max: 8192, default: 4096, description: 'Maximum tokens to generate', required: true },
    thinking_enabled: { type: 'toggle', label: 'Extended Thinking', default: false, description: 'Enable Claude extended thinking for deeper reasoning. Uses output tokens; temperature/top_p are ignored when on. Not shown for Claude Fable and Claude Opus 5.5, which always reason adaptively. On Claude Sonnet 5.5 and Claude Haiku 5.5, off applies at Reasoning Effort High or below; at Extra high or Max they keep thinking.', dependsOn: { field: 'model', values: ['claude-sonnet-5-5', 'claude-haiku-5-5', 'claude-opus-4-8', 'claude-opus-4-7', 'claude-opus-4-6', 'claude-sonnet-5', 'claude-sonnet-4-6', 'claude-sonnet-4-5', 'claude-haiku-4-5', 'claude-opus-4-5'] } },
    effort: { type: 'select', label: 'Reasoning Effort', options: [{ value: 'low', label: 'Low' }, { value: 'medium', label: 'Medium' }, { value: 'high', label: 'High' }, { value: 'xhigh', label: 'Extra high' }, { value: 'max', label: 'Max' }], default: 'high', description: 'How hard Claude reasons and how many tokens it spends overall (output_config.effort). Higher = better quality, more cost/latency. xhigh needs Opus 4.7+/Sonnet 5+/Haiku 5.5/Fable; max excludes Opus 4.5. Levels the chosen model does not support are clamped down to the highest it allows.', dependsOn: { field: 'model', values: ['claude-fable-5-1', 'claude-opus-5-5', 'claude-sonnet-5-5', 'claude-haiku-5-5', 'claude-fable-5', 'claude-opus-4-8', 'claude-opus-4-7', 'claude-opus-4-6', 'claude-opus-4-5', 'claude-sonnet-5', 'claude-sonnet-4-6'] } },
    ...SANDBOX_AGENT_FIELDS,
    cache_ttl: { type: 'select', label: 'System Prompt Cache TTL', options: [{ value: '1h', label: '1 hour (recommended)' }, { value: '5m', label: '5 minutes' }, { value: 'off', label: 'Disabled' }], default: '1h', description: 'Caches system prompt and tool definitions (shared across all conversations on this agent). 1h amortizes the 2× write premium across concurrent conversations.' },
    cache_ttl_history: { type: 'select', label: 'Conversation History Cache TTL', options: [{ value: '5m', label: '5 minutes (recommended)' }, { value: '1h', label: '1 hour' }, { value: 'off', label: 'Disabled' }], default: '5m', description: 'Caches per-conversation history. Primary readers are tool-loop iterations (seconds apart) — 5m captures these at 1.25× write premium instead of 2×. Use 1h only if conversations frequently re-engage between 5min–1h.' },
    streaming_enabled: { type: 'toggle', label: 'Stream Responses', default: false, description: 'Stream responses in real-time (app channel only)' },
    deliver_preamble: { type: 'toggle', label: 'Deliver Pre-Tool Messages', default: true, description: 'Send the short message the model writes before running tools ("Let me check that…") as its own message, so the user hears something during the tool-execution wait. Off = only the final reply is delivered (pre-tool text is discarded). Not applied on email channels; on voice conversations each preamble is spoken as its own audio clip.' },
    file_handling_mode: { type: 'select', label: 'File handling', options: [{ value: 'always_attach', label: 'Attach files' }, { value: 'metadata_only', label: 'Metadata only (retrieve on demand)' }], default: 'always_attach', description: 'No longer changes what the model receives — a conversation\'s earlier files are always announced by path, never re-sent as content. metadata_only still auto-enables read_file so the agent can open one of those paths on demand.' },
    max_attachment_size_mb: { type: 'number', label: 'Max attachment size (MB)', min: 1, description: 'No longer has any effect: earlier files are announced by path rather than attached, so there is no attachment size to cap.' },
    ...COPILOT_TRIGGER_FIELD,
  },
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: {
    api_key: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'Enter a name for this connection', description: 'This name will be used to identify the connection in the system', required: true },
      api_key: { type: 'password', label: 'API Key', placeholder: 'Enter your Anthropic API key', description: 'Your Anthropic API key for Claude chat completions', required: true, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_API_KEY__' },
    },
  },
  token_refresh_config: { api_key: { strategy: 'none' } },
  connector_description: 'LLM provider for Anthropic Claude models. Requires API key.',
};

const googleAiStudio: Connector = {
  connector_id: 'c4d5e6f7-a8b9-0123-cdef-234567890123',
  service_name: 'Google',
  connector_type: 'Agent',
  connector_name: 'Google AI Studio',
  authentication_types: ['api_key'],
  init_endpoint: null,
  unique_per_hub: true,
  account_sid_placeholder: 'Enter email of the account',
  models: ['gemini-3.1-pro-preview', 'gemini-3.8-flash', 'gemini-3.5-flash-lite', 'gemini-3.1-flash-lite', 'gemini-3.5-flash', 'gemini-3.1-flash', 'gemini-2.5-pro', 'gemini-2.5-flash', 'gemini-2.5-flash-lite'],
  is_internal: true,
  is_harness: false,
  connector_module: 'agent',
  enabled: true,
  connector_settings_schema: null,
  agent_settings_schema: {
    model: { type: 'select', label: 'Model', allowCustomValue: true, options: [{ value: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro (Preview)', deprecated: false }, { value: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash', deprecated: false }, { value: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash-Lite', deprecated: false }, { value: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash-Lite', deprecated: false }, { value: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash (Legacy)', deprecated: true }, { value: 'gemini-3.1-flash', label: 'Gemini 3.1 Flash (Legacy)', deprecated: true }, { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro (Legacy)', deprecated: true }, { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash (Legacy)', deprecated: true }, { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash-Lite (Legacy)', deprecated: true }], default: 'gemini-3.8-flash', description: 'Gemini model to use', required: true },
    temperature: { type: 'slider', label: 'Temperature', min: 0, max: 2, default: 1, step: 0.01, description: 'Controls randomness (0=focused, 2=creative)' },
    max_tokens: { type: 'number', label: 'Max Output Tokens', min: 1, max: 8192, default: 4096, description: 'Maximum tokens to generate' },
    reasoning_level: { type: 'select', label: 'Reasoning Level', options: [{ value: 'dynamic', label: 'Dynamic (model default)' }, { value: 'low', label: 'Low' }, { value: 'medium', label: 'Medium' }, { value: 'high', label: 'High' }], default: 'dynamic', description: 'How much the model reasons before answering. Dynamic uses the model default. Gemini 3.x maps to thinking level; Gemini 2.5 to a thinking budget.', dependsOn: { field: 'model', values: ['gemini-3.1-pro-preview', 'gemini-3.8-flash', 'gemini-3.5-flash-lite', 'gemini-3.1-flash-lite', 'gemini-3.5-flash', 'gemini-3.1-pro', 'gemini-3.1-flash', 'gemini-2.5-pro', 'gemini-2.5-flash', 'gemini-2.5-flash-lite'] } },
    streaming_enabled: { type: 'toggle', label: 'Stream Responses', default: false, description: 'Stream responses in real-time (app channel only)' },
    deliver_preamble: { type: 'toggle', label: 'Deliver Pre-Tool Messages', default: true, description: 'Send the short message the model writes before running tools ("Let me check that…") as its own message, so the user hears something during the tool-execution wait. Off = only the final reply is delivered (pre-tool text is discarded). Not applied on email channels; on voice conversations each preamble is spoken as its own audio clip.' },
    file_handling_mode: { type: 'select', label: 'File handling', options: [{ value: 'always_attach', label: 'Attach files' }, { value: 'metadata_only', label: 'Metadata only (retrieve on demand)' }], default: 'always_attach', description: 'No longer changes what the model receives — a conversation\'s earlier files are always announced by path, never re-sent as content. metadata_only still auto-enables read_file so the agent can open one of those paths on demand.' },
    max_attachment_size_mb: { type: 'number', label: 'Max attachment size (MB)', min: 1, description: 'No longer has any effect: earlier files are announced by path rather than attached, so there is no attachment size to cap.' },
    ...SANDBOX_AGENT_FIELDS,
    ...COPILOT_TRIGGER_FIELD,
  },
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: {
    api_key: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'Enter a name for this connection', description: 'This name will be used to identify the connection in the system', required: true },
      api_key: { type: 'password', label: 'API Key', placeholder: 'Enter your Google AI Studio API key', description: 'Your API key from Google AI Studio (aistudio.google.com)', required: true, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_API_KEY__' },
    },
  },
  token_refresh_config: { api_key: { strategy: 'none' } },
  connector_description: 'LLM provider for Google Gemini models. Requires API key.',
};

const openRouter: Connector = {
  connector_id: '4d7e9f23-1a2b-4c3d-9e8f-5a6b7c8d9e0f',
  service_name: 'OpenRouter',
  connector_type: 'Agent',
  connector_name: 'OpenRouter',
  authentication_types: ['api_key'],
  init_endpoint: null,
  unique_per_hub: true,
  account_sid_placeholder: 'Enter email of the account',
  // Jev Latest is the bare `jev-latest`: OpenRouter's decisions endpoint refuses
  // `typesafe/jev-latest` (#4486). `decisions-completion.test.ts` holds every
  // decisions model offered here to an id that endpoint was measured to serve.
  models: ['openai/gpt-6.1-sol', 'openai/gpt-6-astra', 'openai/gpt-6-luna', 'anthropic/claude-fable-5-1', 'anthropic/claude-opus-5-5', 'anthropic/claude-sonnet-5-5', 'anthropic/claude-haiku-5-5', 'google/gemini-3.1-pro-preview', 'google/gemini-3.8-flash', 'google/gemini-3.5-flash-lite', 'google/gemini-3.1-flash-lite', 'x-ai/grok-4.7', 'x-ai/grok-4.6', 'x-ai/grok-4.5', 'x-ai/grok-4.3', 'deepseek/deepseek-v4-pro', 'deepseek/deepseek-v4.1-flash', 'minimax/minimax-m3', 'moonshotai/kimi-k3', 'z-ai/glm-5.3', 'z-ai/glm-5-turbo', 'qwen/qwen3.8-max', 'qwen/qwen3.7-plus', 'qwen/qwen3-coder', 'xiaomi/mimo-v2.6-pro', 'jev-latest', 'typesafe/jev-1.13', 'openai/gpt-6-sol', 'openai/gpt-5.6-sol', 'openai/gpt-5.6-terra', 'openai/gpt-5.6-luna', 'openai/gpt-5.5', 'openai/gpt-5.4-mini', 'openai/gpt-5.4-nano', 'openai/gpt-5.4', 'openai/gpt-5.2', 'openai/gpt-5.2-chat-latest', 'openai/gpt-5.1', 'openai/gpt-5', 'openai/gpt-5-mini', 'openai/gpt-5-nano', 'openai/gpt-4.1', 'openai/gpt-4.1-mini', 'anthropic/claude-fable-5', 'anthropic/claude-sonnet-5', 'anthropic/claude-haiku-4-5', 'anthropic/claude-opus-4-8', 'anthropic/claude-opus-4-7', 'anthropic/claude-sonnet-4-6', 'anthropic/claude-sonnet-4-5', 'anthropic/claude-opus-4-6', 'anthropic/claude-opus-4-5', 'anthropic/claude-opus-4.1', 'google/gemini-3.5-flash', 'google/gemini-3.1-flash', 'google/gemini-3-pro', 'google/gemini-3-flash-preview', 'google/gemini-2.5-pro', 'google/gemini-2.5-flash', 'google/gemini-2.5-flash-lite', 'x-ai/grok-4.20', 'x-ai/grok-4.1', 'x-ai/grok-4-heavy', 'x-ai/grok-4-fast', 'x-ai/grok-4', 'deepseek/deepseek-v3.2', 'minimax/minimax-m2.7', 'moonshotai/kimi-k2.6', 'moonshotai/kimi-k2.5', 'z-ai/glm-5.1', 'z-ai/glm-5', 'qwen/qwen3.7-max', 'qwen/qwen3.6-plus', 'xiaomi/mimo-v2.5-pro', 'xiaomi/mimo-v2-pro'],
  is_internal: true,
  is_harness: false,
  connector_module: 'agent',
  enabled: true,
  connector_settings_schema: null,
  agent_settings_schema: {
    model: { type: 'select', label: 'Model', allowCustomValue: true, options: [{ value: 'openai/gpt-6.1-sol', label: 'GPT-6.1 Sol', deprecated: false }, { value: 'openai/gpt-6-astra', label: 'GPT-6 Astra', deprecated: false }, { value: 'openai/gpt-6-luna', label: 'GPT-6 Luna', deprecated: false }, { value: 'anthropic/claude-fable-5-1', label: 'Claude Fable 5.1', deprecated: false }, { value: 'anthropic/claude-opus-5-5', label: 'Claude Opus 5.5', deprecated: false }, { value: 'anthropic/claude-sonnet-5-5', label: 'Claude Sonnet 5.5', deprecated: false }, { value: 'anthropic/claude-haiku-5-5', label: 'Claude Haiku 5.5', deprecated: false }, { value: 'google/gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro (Preview)', deprecated: false }, { value: 'google/gemini-3.8-flash', label: 'Gemini 3.8 Flash', deprecated: false }, { value: 'google/gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash-Lite', deprecated: false }, { value: 'google/gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash-Lite', deprecated: false }, { value: 'x-ai/grok-4.7', label: 'Grok 4.7', deprecated: false }, { value: 'x-ai/grok-4.6', label: 'Grok 4.6', deprecated: false }, { value: 'x-ai/grok-4.5', label: 'Grok 4.5', deprecated: false }, { value: 'x-ai/grok-4.3', label: 'Grok 4.3', deprecated: false }, { value: 'deepseek/deepseek-v4-pro', label: 'DeepSeek V4 Pro', deprecated: false }, { value: 'deepseek/deepseek-v4.1-flash', label: 'DeepSeek V4.1 Flash', deprecated: false }, { value: 'minimax/minimax-m3', label: 'MiniMax M3', deprecated: false }, { value: 'moonshotai/kimi-k3', label: 'Kimi K3', deprecated: false }, { value: 'z-ai/glm-5.3', label: 'GLM-5.3', deprecated: false }, { value: 'z-ai/glm-5-turbo', label: 'GLM-5-Turbo', deprecated: false }, { value: 'qwen/qwen3.8-max', label: 'Qwen3.8 Max', deprecated: false }, { value: 'qwen/qwen3.7-plus', label: 'Qwen3.7 Plus', deprecated: false }, { value: 'qwen/qwen3-coder', label: 'Qwen3 Coder', deprecated: false }, { value: 'xiaomi/mimo-v2.6-pro', label: 'MiMo-V2.6-Pro', deprecated: false }, { value: 'jev-latest', label: 'Jev Latest (decisions)', deprecated: false }, { value: 'typesafe/jev-1.13', label: 'Jev 1.13 (decisions)', deprecated: false }, { value: 'openai/gpt-6-sol', label: 'GPT-6 Sol (Legacy)', deprecated: true }, { value: 'openai/gpt-5.6-sol', label: 'GPT-5.6 Sol (Legacy)', deprecated: true }, { value: 'openai/gpt-5.6-terra', label: 'GPT-5.6 Terra (Legacy)', deprecated: true }, { value: 'openai/gpt-5.6-luna', label: 'GPT-5.6 Luna (Legacy)', deprecated: true }, { value: 'openai/gpt-5.5', label: 'GPT-5.5 (Legacy)', deprecated: true }, { value: 'openai/gpt-5.4-mini', label: 'GPT-5.4 Mini (Legacy)', deprecated: true }, { value: 'openai/gpt-5.4-nano', label: 'GPT-5.4 Nano (Legacy)', deprecated: true }, { value: 'openai/gpt-5.4', label: 'GPT-5.4 (Legacy)', deprecated: true }, { value: 'openai/gpt-5.2', label: 'GPT-5.2 (Legacy)', deprecated: true }, { value: 'openai/gpt-5.2-chat-latest', label: 'GPT-5.2 Chat (Legacy)', deprecated: true }, { value: 'openai/gpt-5.1', label: 'GPT-5.1 (Legacy)', deprecated: true }, { value: 'openai/gpt-5', label: 'GPT-5 (Legacy)', deprecated: true }, { value: 'openai/gpt-5-mini', label: 'GPT-5 Mini (Legacy)', deprecated: true }, { value: 'openai/gpt-5-nano', label: 'GPT-5 Nano (Legacy)', deprecated: true }, { value: 'openai/gpt-4.1', label: 'GPT-4.1 (Legacy)', deprecated: true }, { value: 'openai/gpt-4.1-mini', label: 'GPT-4.1 Mini (Legacy)', deprecated: true }, { value: 'anthropic/claude-fable-5', label: 'Claude Fable 5 (Legacy)', deprecated: true }, { value: 'anthropic/claude-sonnet-5', label: 'Claude Sonnet 5 (Legacy)', deprecated: true }, { value: 'anthropic/claude-haiku-4-5', label: 'Claude Haiku 4.5 (Legacy)', deprecated: true }, { value: 'anthropic/claude-opus-4-8', label: 'Claude Opus 4.8 (Legacy)', deprecated: true }, { value: 'anthropic/claude-opus-4-7', label: 'Claude Opus 4.7 (Legacy)', deprecated: true }, { value: 'anthropic/claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (Legacy)', deprecated: true }, { value: 'anthropic/claude-sonnet-4-5', label: 'Claude Sonnet 4.5 (Legacy)', deprecated: true }, { value: 'anthropic/claude-opus-4-6', label: 'Claude Opus 4.6 (Legacy)', deprecated: true }, { value: 'anthropic/claude-opus-4-5', label: 'Claude Opus 4.5 (Legacy)', deprecated: true }, { value: 'anthropic/claude-opus-4.1', label: 'Claude Opus 4.1 (Legacy)', deprecated: true }, { value: 'google/gemini-3.5-flash', label: 'Gemini 3.5 Flash (Legacy)', deprecated: true }, { value: 'google/gemini-3.1-flash', label: 'Gemini 3.1 Flash (Legacy)', deprecated: true }, { value: 'google/gemini-3-pro', label: 'Gemini 3 Pro (Legacy)', deprecated: true }, { value: 'google/gemini-3-flash-preview', label: 'Gemini 3 Flash (Legacy)', deprecated: true }, { value: 'google/gemini-2.5-pro', label: 'Gemini 2.5 Pro (Legacy)', deprecated: true }, { value: 'google/gemini-2.5-flash', label: 'Gemini 2.5 Flash (Legacy)', deprecated: true }, { value: 'google/gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash-Lite (Legacy)', deprecated: true }, { value: 'x-ai/grok-4.20', label: 'Grok 4.20 (Legacy)', deprecated: true }, { value: 'x-ai/grok-4.1', label: 'Grok 4.1 (Legacy)', deprecated: true }, { value: 'x-ai/grok-4-heavy', label: 'Grok 4 Heavy (Legacy)', deprecated: true }, { value: 'x-ai/grok-4-fast', label: 'Grok 4 Fast (Legacy)', deprecated: true }, { value: 'x-ai/grok-4', label: 'Grok 4 (Legacy)', deprecated: true }, { value: 'deepseek/deepseek-v3.2', label: 'DeepSeek V3.2 (Legacy)', deprecated: true }, { value: 'minimax/minimax-m2.7', label: 'MiniMax M2.7 (Legacy)', deprecated: true }, { value: 'moonshotai/kimi-k2.6', label: 'Kimi K2.6 (Legacy)', deprecated: true }, { value: 'moonshotai/kimi-k2.5', label: 'Kimi K2.5 (Legacy)', deprecated: true }, { value: 'z-ai/glm-5.1', label: 'GLM-5.1 (Legacy)', deprecated: true }, { value: 'z-ai/glm-5', label: 'GLM-5 (Legacy)', deprecated: true }, { value: 'qwen/qwen3.7-max', label: 'Qwen3.7 Max (Legacy)', deprecated: true }, { value: 'qwen/qwen3.6-plus', label: 'Qwen3.6 Plus (Legacy)', deprecated: true }, { value: 'xiaomi/mimo-v2.5-pro', label: 'MiMo-V2.5-Pro (Legacy)', deprecated: true }, { value: 'xiaomi/mimo-v2-pro', label: 'MiMo-V2-Pro (Legacy)', deprecated: true }], default: 'openai/gpt-6.1-sol', description: 'AI model to use', required: true },
    temperature: { type: 'slider', label: 'Temperature', min: 0, max: 2, default: 0.7, step: 0.01, description: 'Controls randomness (0=focused, 2=creative)' },
    top_p: { type: 'slider', label: 'Top P', min: 0, max: 1, default: 1, step: 0.01, description: 'Nucleus sampling parameter' },
    max_tokens: { type: 'number', label: 'Max Tokens', min: 1, default: 4096, description: 'Maximum number of tokens to generate' },
    reasoning_effort: { type: 'select', label: 'Reasoning Effort', options: ['minimal', 'low', 'medium', 'high', 'xhigh', 'max', 'none'], default: 'none', description: 'How much effort reasoning-capable models spend reasoning. None disables the reasoning override. OpenRouter maps the level to the nearest one each model supports, so xhigh/max/minimal are safe across providers.' },
    streaming_enabled: { type: 'toggle', label: 'Stream Responses', default: false, description: 'Stream responses in real-time (app channel only)' },
    deliver_preamble: { type: 'toggle', label: 'Deliver Pre-Tool Messages', default: true, description: 'Send the short message the model writes before running tools ("Let me check that…") as its own message, so the user hears something during the tool-execution wait. Off = only the final reply is delivered (pre-tool text is discarded). Not applied on email channels; on voice conversations each preamble is spoken as its own audio clip.' },
    file_handling_mode: { type: 'select', label: 'File handling', options: [{ value: 'always_attach', label: 'Attach files' }, { value: 'metadata_only', label: 'Metadata only (retrieve on demand)' }], default: 'always_attach', description: 'No longer changes what the model receives — a conversation\'s earlier files are always announced by path, never re-sent as content. metadata_only still auto-enables read_file so the agent can open one of those paths on demand.' },
    max_attachment_size_mb: { type: 'number', label: 'Max attachment size (MB)', min: 1, description: 'No longer has any effect: earlier files are announced by path rather than attached, so there is no attachment size to cap.' },
    ...SANDBOX_AGENT_FIELDS,
    ...COPILOT_TRIGGER_FIELD,
  },
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: {
    api_key: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'Enter a name for this connection', description: 'This name will be used to identify the connection in the system', required: true },
      api_key: { type: 'password', label: 'API Key', placeholder: 'Enter your OpenRouter API key', description: 'Your OpenRouter API key for accessing various LLM models', required: true, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_API_KEY__' },
    },
  },
  token_refresh_config: { api_key: { strategy: 'none' } },
  connector_description: 'Multi-provider LLM gateway with access to OpenAI, Anthropic, Google, xAI, DeepSeek, MiniMax, Kimi, GLM, Qwen, and Xiaomi models.',
};

const xai: Connector = {
  connector_id: '59b0864f-55ec-4284-9af3-62ebd82e33ce',
  service_name: 'Xai',
  connector_type: 'Agent',
  connector_name: 'xAI',
  authentication_types: ['api_key'],
  init_endpoint: null,
  unique_per_hub: true,
  account_sid_placeholder: 'Enter email of the account',
  models: ['grok-4.7', 'grok-4.6', 'grok-4.5', 'grok-4.3', 'grok-4.20-0309-reasoning', 'grok-4.20-0309-non-reasoning', 'grok-4.20-multi-agent-0309'],
  is_internal: true,
  is_harness: false,
  connector_module: 'agent',
  enabled: true,
  connector_settings_schema: null,
  agent_settings_schema: {
    model: { type: 'select', label: 'Model', allowCustomValue: true, options: [{ value: 'grok-4.7', label: 'Grok 4.7', deprecated: false }, { value: 'grok-4.6', label: 'Grok 4.6', deprecated: false }, { value: 'grok-4.5', label: 'Grok 4.5', deprecated: false }, { value: 'grok-4.3', label: 'Grok 4.3', deprecated: false }, { value: 'grok-4.20-0309-reasoning', label: 'Grok 4.20 (reasoning)', deprecated: false }, { value: 'grok-4.20-0309-non-reasoning', label: 'Grok 4.20 (non-reasoning)', deprecated: false }, { value: 'grok-4.20-multi-agent-0309', label: 'Grok 4.20 (multi-agent)', deprecated: false }], default: 'grok-4.7', description: 'AI model to use', required: true },
    temperature: { type: 'slider', label: 'Temperature', min: 0, max: 2, default: 0.7, step: 0.01, description: 'Controls randomness (0=focused, 2=creative)' },
    top_p: { type: 'slider', label: 'Top P', min: 0, max: 1, default: 1, step: 0.01, description: 'Nucleus sampling parameter' },
    max_tokens: { type: 'number', label: 'Max Tokens', min: 1, default: 4096, description: 'Maximum number of tokens to generate' },
    reasoning_effort: { type: 'select', label: 'Reasoning Effort', options: ['low', 'medium', 'high', 'none'], default: 'none', description: 'How much effort reasoning-capable Grok models spend reasoning (sent as the OpenAI-standard top-level reasoning_effort). None omits the parameter; models without reasoning control ignore/reject a non-none value.' },
    streaming_enabled: { type: 'toggle', label: 'Stream Responses', default: false, description: 'Stream responses in real-time (app channel only)' },
    deliver_preamble: { type: 'toggle', label: 'Deliver Pre-Tool Messages', default: true, description: 'Send the short message the model writes before running tools ("Let me check that…") as its own message, so the user hears something during the tool-execution wait. Off = only the final reply is delivered (pre-tool text is discarded). Not applied on email channels; on voice conversations each preamble is spoken as its own audio clip.' },
    file_handling_mode: { type: 'select', label: 'File handling', options: [{ value: 'always_attach', label: 'Attach files' }, { value: 'metadata_only', label: 'Metadata only (retrieve on demand)' }], default: 'always_attach', description: 'No longer changes what the model receives — a conversation\'s earlier files are always announced by path, never re-sent as content. metadata_only still auto-enables read_file so the agent can open one of those paths on demand.' },
    max_attachment_size_mb: { type: 'number', label: 'Max attachment size (MB)', min: 1, description: 'No longer has any effect: earlier files are announced by path rather than attached, so there is no attachment size to cap.' },
    ...SANDBOX_AGENT_FIELDS,
    ...COPILOT_TRIGGER_FIELD,
  },
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: {
    api_key: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'Enter a name for this connection', description: 'This name will be used to identify the connection in the system', required: true },
      api_key: { type: 'password', label: 'API Key', placeholder: 'Enter your xAI API key', description: 'Your xAI API key from console.x.ai', required: true, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_API_KEY__' },
    },
  },
  token_refresh_config: { api_key: { strategy: 'none' } },
  connector_description: 'LLM provider for xAI Grok models. Requires API key.',
};

// ---------------------------------------------------------------------------
// STT Connectors
// ---------------------------------------------------------------------------

const groqStt: Connector = {
  connector_id: '78328cbf-19d5-4310-9c37-fea2d792f356',
  service_name: 'Groq',
  connector_type: 'STT',
  connector_name: 'Groq STT',
  authentication_types: ['api_key'],
  init_endpoint: null,
  unique_per_hub: true,
  account_sid_placeholder: 'Enter email of the account',
  models: null,
  is_internal: true,
  is_harness: false,
  connector_module: 'transcription',
  enabled: true,
  connector_settings_schema: {
    model: { type: 'select', label: 'Model', allowCustomValue: true, options: ['whisper-large-v3', 'whisper-large-v3-turbo'], default: 'whisper-large-v3-turbo', description: 'Whisper model for transcription' },
    language: { type: 'select', label: 'Language', allowCustomValue: true, options: AUDIO_LANGUAGE_OPTIONS, default: '', description: 'ISO-639-1 code of the audio language (leave empty for auto-detect). Improves accuracy + latency when set.' },
    prompt: { type: 'text', label: 'Context Prompt (Optional)', description: 'Provide context or vocabulary hints to improve transcription accuracy (≤224 tokens)', placeholder: 'Medical terminology, proper nouns, etc.' },
  },
  agent_settings_schema: null,
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: {
    api_key: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'Enter a name for this connection', description: 'This name will be used to identify the connection in the system', required: true },
      api_key: { type: 'password', label: 'API Key', placeholder: 'Enter your Groq API key', description: 'Your Groq API key for speech-to-text transcription services', required: true, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_API_KEY__' },
    },
  },
  token_refresh_config: { api_key: { strategy: 'none' } },
  connector_description: "Fast speech-to-text transcription using Groq's Whisper implementation.",
};

const openaiStt: Connector = {
  connector_id: 'c3d4e5f6-7a8b-4c9d-0e1f-2a3b4c5d6e7f',
  service_name: 'Openai',
  connector_type: 'STT',
  connector_name: 'OpenAI STT',
  authentication_types: ['api_key'],
  init_endpoint: null,
  unique_per_hub: true,
  account_sid_placeholder: 'Enter email of the account',
  models: null,
  is_internal: true,
  is_harness: false,
  connector_module: 'transcription',
  enabled: true,
  connector_settings_schema: {
    model: { type: 'select', label: 'Model', allowCustomValue: true, options: ['gpt-4o-mini-transcribe', 'gpt-4o-transcribe'], default: 'gpt-4o-mini-transcribe', description: 'Transcription model (mini is cheaper, full is more accurate)' },
    language: { type: 'select', label: 'Language', allowCustomValue: true, options: AUDIO_LANGUAGE_OPTIONS, default: '', description: 'ISO-639-1 code of the audio language (leave empty for auto-detect).' },
    prompt: { type: 'text', label: 'Context Prompt (Optional)', description: 'Provide context or vocabulary hints to improve transcription accuracy', placeholder: 'Medical terminology, proper nouns, etc.' },
  },
  agent_settings_schema: null,
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: {
    api_key: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'Enter a name for this connection', description: 'This name will be used to identify the connection in the system', required: true },
      api_key: { type: 'password', label: 'API Key', placeholder: 'Enter your OpenAI API key', description: 'Your OpenAI API key for speech-to-text transcription services', required: true, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_API_KEY__' },
    },
  },
  token_refresh_config: { api_key: { strategy: 'none' } },
  connector_description: 'Speech-to-text transcription using OpenAI gpt-4o-transcribe models.',
};

const elevenLabsStt: Connector = {
  connector_id: '8a1a1917-3559-4858-bdef-313991341277',
  service_name: 'ElevenLabs',
  connector_type: 'STT',
  connector_name: 'ElevenLabs STT',
  authentication_types: ['api_key'],
  init_endpoint: null,
  unique_per_hub: true,
  account_sid_placeholder: 'Enter email of the account',
  models: null,
  is_internal: true,
  is_harness: false,
  connector_module: 'transcription',
  enabled: true,
  connector_settings_schema: {
    // scribe_v2 is the current batch model. scribe_v2_realtime is a separate
    // WebSocket API and is not valid on the REST /v1/speech-to-text endpoint we call.
    model: { type: 'select', label: 'Model', allowCustomValue: true, options: ['scribe_v2'], default: 'scribe_v2', description: 'ElevenLabs Scribe model (scribe_v2: state-of-the-art accuracy across 90+ languages)' },
    language: { type: 'select', label: 'Language', allowCustomValue: true, options: AUDIO_LANGUAGE_OPTIONS, default: '', description: 'ISO-639-1 code of the audio language (leave empty for auto-detect). Improves accuracy + latency when set.' },
    tagAudioEvents: { type: 'toggle', label: 'Tag Audio Events', default: true, description: 'Annotate non-speech audio events (e.g. (laughter), (footsteps)) in the transcript. Disable for speech-only text.' },
  },
  agent_settings_schema: null,
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: {
    api_key: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'Enter a name for this connection', description: 'This name will be used to identify the connection in the system', required: true },
      api_key: { type: 'password', label: 'API Key', placeholder: 'Enter your ElevenLabs API key', description: 'Your ElevenLabs API key for speech-to-text transcription services', required: true, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_API_KEY__' },
    },
  },
  token_refresh_config: { api_key: { strategy: 'none' } },
  connector_description: 'Speech-to-text transcription using ElevenLabs Scribe v2 (state-of-the-art accuracy across 90+ languages).',
};

// ---------------------------------------------------------------------------
// TTS Connectors
// ---------------------------------------------------------------------------

const openaiTts: Connector = {
  connector_id: 'b2c3d4e5-f6a7-4b89-c012-3456789abcdf',
  service_name: 'Openai',
  connector_type: 'TTS',
  connector_name: 'OpenAI TTS',
  authentication_types: ['api_key'],
  init_endpoint: null,
  unique_per_hub: true,
  account_sid_placeholder: 'Enter email of the account',
  models: null,
  is_internal: true,
  is_harness: false,
  connector_module: 'tts',
  enabled: true,
  connector_settings_schema: {
    ...TTS_VOICE_REPLY_ENABLED_FIELD,
    voice: { type: 'select', label: 'Voice', options: ['alloy', 'ash', 'ballad', 'coral', 'echo', 'fable', 'nova', 'onyx', 'sage', 'shimmer', 'verse'], default: 'alloy', description: 'Voice to use for speech synthesis' },
    model: { type: 'select', label: 'Model', allowCustomValue: true, options: ['gpt-4o-mini-tts', 'tts-1', 'tts-1-hd'], default: 'gpt-4o-mini-tts', description: 'TTS model to use' },
    speed: { type: 'slider', label: 'Speed', min: 0.25, max: 4.0, default: 1.0, step: 0.25, description: 'Speech speed multiplier' },
    instructions: { type: 'text', label: 'Instructions (Advanced)', description: 'Provide guidance on how the AI should speak. Only available for gpt-4o-mini-tts model.', dependsOn: { field: 'model', values: ['gpt-4o-mini-tts'] } },
  },
  agent_settings_schema: null,
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: {
    api_key: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'Enter a name for this connection', description: 'This name will be used to identify the connection in the system', required: true },
      api_key: { type: 'password', label: 'API Key', placeholder: 'Enter your OpenAI API key', description: 'Your OpenAI API key for text-to-speech services', required: true, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_API_KEY__' },
    },
  },
  token_refresh_config: { api_key: { strategy: 'none' } },
  connector_description: 'Text-to-speech synthesis using OpenAI voices (alloy, ash, ballad, coral, echo, fable, nova, onyx, sage, shimmer, verse).',
};

const groqTts: Connector = {
  connector_id: 'd7e8f9a0-1b2c-4d3e-8f40-5a6b7c8d9e0f',
  service_name: 'Groq',
  connector_type: 'TTS',
  connector_name: 'Groq TTS',
  authentication_types: ['api_key'],
  init_endpoint: null,
  unique_per_hub: true,
  account_sid_placeholder: 'Enter email of the account',
  models: null,
  is_internal: true,
  is_harness: false,
  connector_module: 'tts',
  enabled: true,
  connector_settings_schema: {
    ...TTS_VOICE_REPLY_ENABLED_FIELD,
    model: { type: 'select', label: 'Model', allowCustomValue: true, options: ['canopylabs/orpheus-v1-english', 'canopylabs/orpheus-arabic-saudi'], default: 'canopylabs/orpheus-v1-english', description: 'Orpheus TTS model (Preview on Groq)' },
    voice: { type: 'text', label: 'Voice', default: 'hannah', description: 'Voice name. English: troy, hannah, austin. Arabic: see Groq docs.' },
  },
  agent_settings_schema: null,
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: {
    api_key: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'Enter a name for this connection', description: 'This name will be used to identify the connection in the system', required: true },
      api_key: { type: 'password', label: 'API Key', placeholder: 'Enter your Groq API key', description: 'Your Groq API key for text-to-speech services', required: true, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_API_KEY__' },
    },
  },
  token_refresh_config: { api_key: { strategy: 'none' } },
  connector_description: 'Expressive text-to-speech using Canopy Labs Orpheus models via Groq (Preview).',
};

const elevenLabsTts: Connector = {
  connector_id: 'a1b2c3d4-e5f6-4789-a012-3456789abcde',
  service_name: 'ElevenLabs',
  connector_type: 'TTS',
  connector_name: 'ElevenLabs TTS',
  authentication_types: ['api_key'],
  init_endpoint: null,
  unique_per_hub: true,
  account_sid_placeholder: 'Enter email of the account',
  models: null,
  is_internal: true,
  is_harness: false,
  connector_module: 'tts',
  enabled: true,
  connector_settings_schema: {
    ...TTS_VOICE_REPLY_ENABLED_FIELD,
    voiceId: { type: 'text', label: 'Voice ID', default: 'pNInz6obpgDQGcFmaJgB', description: 'ElevenLabs voice ID (default: Adam)' },
    modelId: { type: 'select', label: 'Model', allowCustomValue: true, options: ['eleven_flash_v2_5', 'eleven_turbo_v2_5', 'eleven_v3', 'eleven_multilingual_v2'], default: 'eleven_flash_v2_5', description: 'TTS model (flash_v2_5: low latency; turbo_v2_5: balanced quality/latency; v3: flagship expressive; multilingual_v2: narration/audiobook quality)' },
    stability: { type: 'slider', label: 'Stability', min: 0, max: 1, default: 0.5, step: 0.1, description: 'Voice stability (lower = more variable)' },
    similarityBoost: { type: 'slider', label: 'Clarity + Similarity', min: 0, max: 1, default: 0.75, step: 0.1, description: 'Boosts voice clarity and similarity' },
    style: { type: 'slider', label: 'Style Exaggeration', min: 0, max: 1, default: 0, step: 0.05, description: 'Style exaggeration (0 = none, most stable). Higher is more expressive but can reduce stability and add latency; ignored by some models (e.g. eleven_v3).' },
    useSpeakerBoost: { type: 'toggle', label: 'Speaker Boost', default: true, description: 'Boost similarity to the original voice (slightly higher latency).' },
    speed: { type: 'slider', label: 'Speed', min: 0.25, max: 4.0, default: 1.0, step: 0.25, description: 'Speech speed multiplier' },
    languageCode: { type: 'select', label: 'Language', allowCustomValue: true, options: AUDIO_LANGUAGE_OPTIONS, default: '', description: 'ISO-639-1 code to force output language (leave empty to let the model decide from input text). API returns an error if the selected model does not support the code.' },
  },
  agent_settings_schema: null,
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: {
    api_key: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'Enter a name for this connection', description: 'This name will be used to identify the connection in the system', required: true },
      api_key: { type: 'password', label: 'API Key', placeholder: 'Enter your ElevenLabs API key', description: 'Your ElevenLabs API key for text-to-speech services', required: true, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_API_KEY__' },
    },
  },
  token_refresh_config: { api_key: { strategy: 'none' } },
  connector_description: 'High-quality text-to-speech with custom voice cloning via ElevenLabs.',
};

const GEMINI_TTS_VOICES = ['Achernar', 'Achird', 'Algenib', 'Algieba', 'Alnilam', 'Aoede', 'Autonoe', 'Callirrhoe', 'Charon', 'Despina', 'Enceladus', 'Erinome', 'Fenrir', 'Gacrux', 'Iapetus', 'Kore', 'Laomedeia', 'Leda', 'Orus', 'Pulcherrima', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Sulafat', 'Umbriel', 'Vindemiatrix', 'Zephyr', 'Zubenelgenubi'];

const googleTts: Connector = {
  connector_id: '54448ab4-7457-436d-ae1a-4d7713130cb9',
  service_name: 'Google',
  connector_type: 'TTS',
  connector_name: 'Google AI Studio TTS',
  authentication_types: ['api_key'],
  init_endpoint: null,
  unique_per_hub: true,
  account_sid_placeholder: 'Enter email of the account',
  models: null,
  is_internal: true,
  is_harness: false,
  connector_module: 'tts',
  enabled: true,
  connector_settings_schema: {
    ...TTS_VOICE_REPLY_ENABLED_FIELD,
    model: { type: 'select', label: 'Model', allowCustomValue: true, options: ['gemini-3.8-flash-lite-tts', 'gemini-3.8-flash-tts'], default: 'gemini-3.8-flash-lite-tts', description: 'TTS model (flash-lite-tts: fast and low-cost, for everyday speech in major languages; flash-tts: highest fidelity and expressiveness, 130 languages)' },
    voice: { type: 'select', label: 'Voice', allowCustomValue: true, options: GEMINI_TTS_VOICES, default: 'Kore', description: 'Prebuilt voice, or the ID of a voice from the Extended Voice Library or one you designed in Google AI Studio' },
    instructions: { type: 'text', label: 'Speaking Style (Advanced)', description: 'How the voice should speak across every reply: emotion, delivery, pacing, volume (e.g. "warm and unhurried, like a friendly receptionist").' },
  },
  agent_settings_schema: null,
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: {
    api_key: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'Enter a name for this connection', description: 'This name will be used to identify the connection in the system', required: true },
      api_key: { type: 'password', label: 'API Key', placeholder: 'Enter your Google AI Studio API key', description: 'Your API key from Google AI Studio (aistudio.google.com)', required: true, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_API_KEY__' },
    },
  },
  token_refresh_config: { api_key: { strategy: 'none' } },
  connector_description: 'Expressive text-to-speech with Gemini TTS models via Google AI Studio (30 voices, style prompts, 100+ languages).',
};

// ---------------------------------------------------------------------------
// Native Tool Connectors
// ---------------------------------------------------------------------------

const wayai: ToolConnector = {
  connector_id: 'b17d9f3a-4e1b-46c9-b648-a2f0c3611aa4',
  service_name: 'Wayai',
  connector_type: 'Tool',
  tool_variant: 'native',
  connector_name: 'Wayai',
  authentication_types: ['none'],
  init_endpoint: null,
  unique_per_hub: true,
  account_sid_placeholder: null,
  models: null,
  is_internal: true,
  is_harness: false,
  connector_module: 'native/wayai',
  enabled: true,
  connector_settings_schema: null,
  agent_settings_schema: null,
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: null,
  token_refresh_config: { none: { strategy: 'none' } },
  connector_description: 'Native tools for conversation management, file handling, skills, and tool orchestration.',
};

const externalResources: ToolConnector = {
  connector_id: 'e8f9a0b1-2c3d-4e5f-6789-0abcdef12345',
  service_name: 'Wayai',
  connector_type: 'Tool',
  tool_variant: 'native',
  connector_name: 'External Resources',
  authentication_types: ['api_key'],
  init_endpoint: null,
  unique_per_hub: false,
  account_sid_placeholder: 'External Storage API URL',
  models: null,
  is_internal: true,
  is_harness: false,
  connector_module: 'native/external_resources',
  enabled: true,
  connector_settings_schema: null,
  agent_settings_schema: null,
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: {
    api_key: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'Enter a name for this connection', description: 'This name will be used to identify the connection in the system', required: true },
      base_url: { type: 'text', label: 'Storage API URL', placeholder: 'https://storage.example.com/api', description: 'External storage service API endpoint', required: true },
      api_key: { type: 'password', label: 'API Key', placeholder: 'Enter your storage service API key', required: true, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_API_KEY__' },
    },
  },
  token_refresh_config: { api_key: { strategy: 'none' } },
  connector_description: 'Connect to external file storage services.',
};

// ---------------------------------------------------------------------------
// Custom Tool Connectors
// ---------------------------------------------------------------------------

const restApiTool: ToolConnector = {
  connector_id: 'b15fb991-63e1-4a79-a174-d10aa66f4414',
  service_name: 'Other',
  connector_type: 'Tool',
  tool_variant: 'custom',
  connector_name: 'REST API',
  legacy_names: ['User Tool'], // renamed from 'User Tool'; old YAML `service:` must keep resolving
  authentication_types: ['api_key', 'basic_auth', 'bearer'],
  init_endpoint: null,
  unique_per_hub: false,
  account_sid_placeholder: 'Account identifier',
  models: null,
  is_internal: false,
  is_harness: false,
  connector_module: null,
  enabled: true,
  connector_settings_schema: null,
  agent_settings_schema: null,
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: {
    api_key: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'Enter a name for this connection', description: 'This name will be used to identify the connection in the system', required: true },
      base_url: { type: 'text', label: 'Base URL', placeholder: 'https://api.example.com', description: 'Base URL for the API connection (used as prefix for all tool paths)' },
      api_key: { type: 'password', label: 'API Key', placeholder: 'Enter your API key', required: true, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_API_KEY__' },
      access_token: { type: 'password', label: 'Access Token / Service Key (Optional)', placeholder: 'Enter if your API requires a second credential', description: 'Optional: Some APIs require both an API key and an access token/service key', maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_ACCESS_TOKEN__' },
      connection_headers: { type: 'custom', component: 'KeyValueGrid', label: 'Connection Headers', description: 'Headers to include in all requests for this connection' },
    },
    basic_auth: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'Enter a name for this connection', description: 'This name will be used to identify the connection in the system', required: true },
      base_url: { type: 'text', label: 'Base URL', placeholder: 'https://api.example.com', description: 'Base URL for the API connection (used as prefix for all tool paths)' },
      username: { type: 'text', label: 'Username', placeholder: 'Enter username', required: true },
      password: { type: 'password', label: 'Password', placeholder: 'Enter password', required: true, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_PASSWORD__' },
      connection_headers: { type: 'custom', component: 'KeyValueGrid', label: 'Connection Headers', description: 'Headers to include in all requests for this connection' },
    },
    bearer: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'Enter a name for this connection', description: 'This name will be used to identify the connection in the system', required: true },
      base_url: { type: 'text', label: 'Base URL', placeholder: 'https://api.example.com', description: 'Base URL for the API connection (used as prefix for all tool paths)' },
      access_token: { type: 'password', label: 'Bearer Token', placeholder: 'Enter your bearer token', required: true, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_ACCESS_TOKEN__' },
      connection_headers: { type: 'custom', component: 'KeyValueGrid', label: 'Connection Headers', description: 'Headers to include in all requests for this connection', defaultValue: { Authorization: 'Bearer {{access_token}}' } },
    },
  },
  token_refresh_config: { api_key: { strategy: 'none' }, basic_auth: { strategy: 'none' }, bearer: { strategy: 'none' } },
  connector_description: 'Connect custom REST APIs with API key, basic auth, or bearer token authentication.',
};

// ---------------------------------------------------------------------------
// MCP Connector
// ---------------------------------------------------------------------------

const mcpServer: ToolConnector = {
  connector_id: 'f1a2b3c4-d5e6-7890-abcd-ef1234567890',
  service_name: 'Other',
  connector_type: 'Tool',
  tool_variant: 'mcp',
  connector_name: 'MCP Server',
  // MCP's authorization spec is OAuth 2.1; the non-OAuth fallback that most
  // servers use is a static token sent as `Authorization: Bearer <token>`
  // (RFC 6750) — i.e. a Bearer Token, not an "API Key". Modeled as `bearer` so
  // it binds a Bearer Token org credential, matching the field label and wire.
  authentication_types: ['bearer', 'oauth'],
  init_endpoint: 'mcp_oauth_callback',
  unique_per_hub: false,
  account_sid_placeholder: 'MCP Server URL',
  models: null,
  is_internal: true,
  is_harness: false,
  connector_module: 'native/mcp_client',
  enabled: true,
  connector_settings_schema: {
    // Turn-time tool-schema freshness. The agent resolves this server's tool
    // schemas live at turn time from a TTL cache (stale-while-revalidate), so an
    // upstream add/edit/remove reflects on the next turn without a manual sync.
    // This is the fallback window when the server doesn't send its own `ttlMs`
    // (MCP 2026-07-28 SEP-2549, which takes precedence). `0` = always fetch fresh
    // every turn (the strict escape hatch; highest latency). Default mirrors
    // DEFAULT_MCP_TOOL_SCHEMA_TTL_MS in durable-objects/mcp-session.ts.
    mcp_tool_schema_ttl_ms: {
      type: 'number',
      label: 'Tool schema cache (ms)',
      default: 60000,
      min: 0,
      description:
        "How long to cache this server's tool schemas before re-fetching at turn time. 0 = always fetch fresh every turn (highest latency). The server's own freshness hint overrides this when present.",
    },
  },
  agent_settings_schema: null,
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: {
    bearer: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'My MCP Server', description: 'A friendly name to identify this MCP connection', required: true },
      base_url: { type: 'text', label: 'MCP Server URL', placeholder: 'https://mcp.example.com/mcp', description: 'The Streamable HTTP endpoint of the MCP server (any protocol revision — 2026-07-28 and earlier are both supported)', required: true },
      access_token: { type: 'password', label: 'Bearer Token', placeholder: 'Enter your bearer token (optional)', description: 'Optional - Leave empty if the MCP server does not require authentication', required: false, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_ACCESS_TOKEN__' },
      connection_headers: { type: 'custom', component: 'KeyValueGrid', label: 'Custom Headers', description: 'Additional headers to include in MCP requests' },
    },
    oauth: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'My MCP Server (OAuth)', description: 'A friendly name to identify this MCP connection', required: true },
      base_url: { type: 'text', label: 'MCP Server URL', placeholder: 'https://mcp.example.com', description: 'Base URL of the MCP server - OAuth metadata will be discovered automatically', required: true },
      client_id: { type: 'text', label: 'OAuth Client ID', placeholder: 'your-client-id', description: 'Optional - Required only if the MCP server cannot register a client for us. That client must have this hub-specific redirect URI registered: https://api.wayai.pro/oauth/callback/mcp/<hub id>, where <hub id> is the id in the settings URL. There is no longer one shared callback URI.', required: false },
      client_secret: { type: 'password', label: 'OAuth Client Secret', placeholder: 'your-client-secret', description: 'Optional - Required only if the MCP server requires a client secret', required: false },
    },
  },
  token_refresh_config: { bearer: { strategy: 'none' }, oauth: { strategy: 'refresh_token_flow', access_token: { refresh_window_hours: 1 }, refresh_token: { has_expiry: false } } },
  connector_description: 'Connect to external MCP servers with a Bearer Token or OAuth 2.0 authentication. The protocol revision is negotiated per connection, so stateless (2026-07-28) and older servers both work without configuration.',
};

// ---------------------------------------------------------------------------
// Base Toolset Connector (wayai-pro/wayai#5083, Track K)
// ---------------------------------------------------------------------------

/**
 * The `service_name` WayAI recognises a toolset connection by. A code name, never displayed;
 * `isRekorToolsetConnector` (catalog index) is its one reader.
 */
export const REKOR_TOOLSET_SERVICE_NAME = 'RekorToolset';

// A connection to one toolset of a base, holding a toolset-bound token and nothing else (no OAuth,
// no refresh). Every write of that token resolves it and records what it reaches as the
// server-owned `binding` setting (`apps/backend/src/rekor/connection-write-hook.ts`), which is
// how a hub's toolset connections are held to their own environment and how a customer-facing
// agent is kept off a toolset that reaches more than files or a customer scope admits
// (`@wayai/core/contracts` `rekor-toolset.ts`).
//
// `tool_variant: 'native'`, not `mcp`: an `mcp` connection is discovered and called with its
// stored token, while a native agent reaches its toolset over the service binding. Its tools run in
// `native/rekor_toolset`, one bound attested call each, and are discovered from the toolset's own
// tool definitions into the hub's `mcp_tool` catalog. `is_internal: true` is what routes them there:
// dispatch follows it, and an external HTTP call would carry the stored token.
//
// Its credential belongs to its own hub: publish, sync and replicate never copy its token, its
// credential link or its binding (`credentialStaysInItsHub` in `hub-branching.ts`), because a
// preview hub's token reaches a preview base and a production hub's a production base.
const rekorToolset: ToolConnector = {
  connector_id: '2cf279bf-29ae-4983-a67f-bf619e391586',
  service_name: REKOR_TOOLSET_SERVICE_NAME,
  connector_type: 'Tool',
  tool_variant: 'native',
  connector_name: 'Base Toolset',
  authentication_types: ['bearer'],
  init_endpoint: null,
  unique_per_hub: false,
  account_sid_placeholder: null,
  models: null,
  is_internal: true,
  is_harness: false,
  connector_module: 'native/rekor_toolset',
  enabled: true,
  connector_settings_schema: {
    toolset: { type: 'text', label: 'Toolset', default: '', placeholder: 'support', description: 'The toolset this connection reaches: its slug, as in its MCP URL (…/t/<slug>/mcp). The token must be bound to it.', required: true },
  },
  agent_settings_schema: null,
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: {
    bearer: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'Support knowledge', description: 'This name will be used to identify the connection in the system', required: true },
      access_token: { type: 'password', label: 'Toolset Token', placeholder: 'rec_…', description: 'An API token bound to this toolset. It must be for a base of this hub\'s environment: a preview hub holds a preview base\'s token, a production hub a production base\'s.', required: true, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_ACCESS_TOKEN__' },
    },
  },
  token_refresh_config: { bearer: { strategy: 'none' } },
  connector_description: 'Give agents one toolset of a base: its tools, through a token bound to that toolset.',
};

// ---------------------------------------------------------------------------
// Harness Agent connectors — `is_harness: true` is the single dispatch
// discriminant (PR 2.2b): the whole turn is delegated to an external agent loop.
// ---------------------------------------------------------------------------

const CLAUDE_HARNESS_MODELS = ['claude-opus-5-5', 'claude-opus-4-8', 'claude-opus-4-7', 'claude-sonnet-5', 'claude-sonnet-4-6', 'claude-haiku-4-5'];

const CLAUDE_HARNESS_MODEL_OPTIONS = [{ value: 'claude-opus-5-5', label: 'Claude Opus 5.5' }, { value: 'claude-opus-4-8', label: 'Claude Opus 4.8' }, { value: 'claude-opus-4-7', label: 'Claude Opus 4.7' }, { value: 'claude-sonnet-5', label: 'Claude Sonnet 5' }, { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' }, { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5' }];

const HARNESS_MCP_SERVERS_FIELD = {
  mcp_servers: { type: 'array', itemType: 'text', maxItems: 32, label: 'MCP Servers', description: 'MCP server connection IDs (on this hub) the agent may call from inside the sandbox.' },
};

const HARNESS_EGRESS_FIELDS = {
  network_egress_policy: { type: 'select', label: 'Network Egress', options: [{ value: 'allowlist', label: 'Allowlist (deny-all unless listed)' }, { value: 'isolated', label: 'Isolated (control host only)' }, { value: 'open', label: 'Open (internet)' }], default: 'allowlist', description: 'Sandbox egress posture. Empty allowlist ⇒ deny-all.' },
  network_egress_allowlist: { type: 'array', itemType: 'text', maxItems: 64, label: 'Egress Allowlist', description: 'Hosts reachable under Allowlist, or the single control host under Isolated. Ignored under Open.' },
};

// ---------------------------------------------------------------------------
// Claude Agent (Managed) — the harness turn seam driven by Anthropic's
// Managed Agents API (beta `managed-agents-2026-04-01`): Anthropic hosts BOTH
// the agent loop and the sandbox, and no credential ever enters the
// sandbox — the Anthropic key stays in the Worker, and
// the optional GitHub PAT is applied by Anthropic's git proxy after requests
// leave the sandbox. The PAT reuses the `access_token` credential field
// (precedent: restApiTool) so encryption, contracts, and the
// production-credential endpoint work with zero plumbing changes. `enabled`
// (selectable) — execution still gates behind the platform-wide default-deny
// harness flag, which doubles as the per-turn kill switch; `beta` marks it Beta
// while the API header is beta.
// ---------------------------------------------------------------------------

const claudeManagedAgents: Connector = {
  connector_id: '4b8eeecf-a835-444a-abed-516d0a29cee2',
  service_name: 'ClaudeManagedAgents',
  connector_type: 'Agent',
  connector_name: 'Claude Agent (Managed)',
  authentication_types: ['api_key'],
  init_endpoint: null,
  unique_per_hub: false,
  account_sid_placeholder: null,
  models: CLAUDE_HARNESS_MODELS,
  is_internal: true,
  is_harness: true,
  connector_module: 'harness',
  enabled: true,
  beta: true,
  connector_settings_schema: null,
  agent_settings_schema: {
    model: { type: 'select', label: 'Model', allowCustomValue: true, options: CLAUDE_HARNESS_MODEL_OPTIONS, default: 'claude-sonnet-5', description: 'Claude model the Anthropic-hosted managed agent loop drives.', required: true },
    ...HARNESS_MCP_SERVERS_FIELD,
    // The managed session is conversation-lifetime and is NOT reaped at this deadline.
    max_session_minutes: { type: 'number', label: 'Max Turn (minutes)', min: 1, max: 480, default: 60, description: 'Hard cap on a single turn\'s drain wall-clock, and the bound on the per-turn harness token TTL. The managed session itself lives for the conversation — it is not reaped at this deadline.' },
    ...HARNESS_EGRESS_FIELDS,
    // repo_url only, with no `repo_branch`. The Managed Agents
    // `github_repository` resource exposes no branch/ref/checkout field, so a
    // `repo_branch` setting could not be honoured; per this connector's own rule it is
    // ABSENT rather than silently ignored. Working on a non-default branch is an
    // agent-instruction concern (the agent checks out via git / the GitHub MCP server).
    repo_url: { type: 'text', label: 'Repository URL', description: 'Optional git repository cloned into the managed workspace at session start. The managed API clones the DEFAULT branch and exposes no branch setting — to work on another branch, instruct the agent to check it out.' },
    ...COPILOT_TRIGGER_FIELD,
  },
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: {
    api_key: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'Enter a name for this connection', description: 'This name will be used to identify the connection in the system', required: true },
      api_key: { type: 'password', label: 'API Key', placeholder: 'Enter your Anthropic API key', description: 'Anthropic API key for the Managed Agents API. Used only from the WayAI Worker — no sandbox ever sees it.', required: true, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_API_KEY__' },
      access_token: { type: 'password', label: 'GitHub Token (Optional)', placeholder: 'Enter a GitHub personal access token', description: 'Optional: personal access token for the repo_url checkout. Anthropic\'s git proxy injects it after requests leave the sandbox — it never enters the sandbox.', maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_ACCESS_TOKEN__' },
    },
  },
  token_refresh_config: { api_key: { strategy: 'none' } },
  connector_description: 'Harness agent powered by Anthropic\'s Managed Agents API (Beta) — Anthropic hosts both the agent loop and the sandbox. Delegates the full turn to the managed loop; requires only an Anthropic API key.',
};

// ---------------------------------------------------------------------------
// Realtime — the voice layer of a live call (realtime voice calls, phase 1).
//
// NOT an LLM connection: no turn runs on it. The provider's model listens to the caller and
// speaks, and it delegates substance to the conversation's pilot-track agent. It is configured
// per voice agent: a `pilot_voice` agent binds only a Realtime connection, and a Realtime
// connection binds only a `pilot_voice` agent (`voiceAgentBindingRefusal`, `./voice-agent.ts`),
// judged at the HubDO agent and connection writers. `handleAgentConnector` never binds a
// Realtime connection to an agent (it acts on `Agent` connections only), and never binds an
// `Agent` connection to a `pilot_voice` one.
//
// DARK while the voice-calls gate is closed (`voiceCallsEnabled`, PlatformDO): the
// connections "available to Add" listing hides it, and the HubDO connection-write chokepoint
// refuses to create one, or to turn one on (`hub-connection-ops.ts`). `enabled: true` only
// makes it selectable while the gate is open.
//
// The credential is the org's OpenAI PROJECT key (BYOK). The backend creates each call's
// provider session with it and attaches call control with it; the caller receives only
// the provider's SDP answer, never a credential. It is read only through
// `resolveConnectionSecret` / `resolveConnectionSecretFromHub`.
// ---------------------------------------------------------------------------

/**
 * The most characters one of the voice's fixed lines may hold as the builder writes it: about
 * 20 seconds of speech, and well inside one provider append (`APPEND_MAX_CHARS`, 1,200).
 */
const SPOKEN_LINE_MAX_CHARS = 300;

/** One of the voice's fixed lines as a setting: empty is the platform's line. */
function spokenLineField(label: string, description: string) {
  return { type: 'text', label, maxLength: SPOKEN_LINE_MAX_CHARS, default: '', description } as const;
}

const gptLive: Connector = {
  connector_id: '01e7b19c-bc94-43f4-a780-ad5a22fb7127',
  service_name: 'Openai',
  connector_type: 'Realtime',
  connector_name: 'OpenAI GPT-Live',
  authentication_types: ['api_key'],
  init_endpoint: null,
  unique_per_hub: true,
  account_sid_placeholder: null,
  // One snapshot, fixed for the session.
  models: ['gpt-live-1'],
  is_internal: true,
  is_harness: false,
  connector_module: 'realtime',
  enabled: true,
  connector_settings_schema: null,
  // Settings of the voice agent that uses this connection. `speaker_voice`, never `voice`:
  // `voice` is in that role's own name (`pilot_voice`).
  agent_settings_schema: {
    speaker_voice: { type: 'select', label: 'Voice', allowCustomValue: true, options: ['bossa', 'tempo'], default: 'bossa', description: 'The built-in GPT-Live voice the caller hears. bossa and tempo are Brazilian Portuguese voices; another built-in voice id can be typed.' },
    language: { type: 'select', label: 'Language', options: [{ value: '', label: 'Hub language' }, { value: CALLER_LANGUAGE, label: 'Caller\'s language' }, { value: 'pt', label: 'Portuguese' }, { value: 'en', label: 'English' }, { value: 'es', label: 'Spanish' }], default: '', description: 'The language the voice speaks with the caller. Empty uses the hub\'s language; Caller\'s language uses the language in the caller\'s profile, or the hub\'s when they have none.' },
    // The provider ends every session about 2 hours after it starts (`expires_at` is
    // start + 7,199 s), so 119 whole minutes is the most a call can last.
    max_call_minutes: { type: 'number', label: 'Maximum Call Length (minutes)', min: 1, max: 119, default: 10, description: 'A call ends when it reaches this length.' },
    inactivity_timeout_seconds: { type: 'number', label: 'Inactivity Timeout (seconds)', min: 10, max: 600, default: 60, description: 'A call ends after this many seconds in which neither side speaks. A wait for the hub\'s agent to answer, up to the answer timeout, does not count.' },
    delegation_timeout_seconds: { type: 'number', label: 'Answer Timeout (seconds)', min: 5, max: 120, default: 30, description: 'How long the voice waits for the hub\'s agent to answer one question before it gives up on that answer.' },
    // The fixed lines the voice says (`call-texts.ts`), each the builder's wording when set.
    // Read by the call route (`resolveVoiceCallSettings`), clipped to `maxLength` again there,
    // and used as written: placeholders are not filled.
    greeting_text: spokenLineField('Greeting', 'What the voice says when the call starts, in the call\'s language. Empty uses WayAI\'s greeting, which names the hub.'),
    progress_cues: { type: 'toggle', label: 'Progress Cues', default: true, description: 'While the hub\'s agent works on an answer, the voice says a short "still checking" line about 5 and 10 seconds after the caller stops speaking. Off: the wait is silent unless the voice\'s instructions fill it; the answer timeout line still plays.' },
    first_progress_cue_text: spokenLineField('First Progress Cue', 'What the voice says about 5 seconds into a wait for an answer. Empty uses WayAI\'s line in the call\'s language.'),
    second_progress_cue_text: spokenLineField('Second Progress Cue', 'What the voice says about 10 seconds into a wait for an answer. Empty uses WayAI\'s line in the call\'s language.'),
    please_repeat_text: spokenLineField('Please Repeat Line', 'What the voice says when it has no words for the caller\'s question and asks them to repeat it. Empty uses WayAI\'s line in the call\'s language.'),
    turn_failed_text: spokenLineField('Couldn\'t Get That Line', 'What the voice says when the hub\'s agent could not produce an answer. Empty uses WayAI\'s apology in the call\'s language.'),
    timed_out_text: spokenLineField('Answer Timeout Line', 'What the voice says when an answer is not back within the answer timeout. Empty uses WayAI\'s apology in the call\'s language.'),
    // Opt-in (D7): off unless turned on. Read by the call route (`resolveVoiceCallSettings`).
    record_calls: { type: 'toggle', label: 'Record Calls', default: false, description: 'Keep each call\'s audio, both sides, as a recording only your team can play. Before a live call starts, the caller is warned that it will be recorded and chooses whether to go on; your team plays it from the conversation. An eval call (wayai eval call) is recorded with no warning, since its caller is your own machine; your team plays it from the eval call\'s page.' },
  },
  channel_settings_schema: null,
  tool_settings_schema: null,
  connector_credential_schema: {
    api_key: {
      connection_display_name: { type: 'text', label: 'Connection Name', placeholder: 'Enter a name for this connection', description: 'This name will be used to identify the connection in the system', required: true },
      api_key: { type: 'password', label: 'Project API Key', placeholder: 'Enter your OpenAI project API key', description: 'API key of the OpenAI project your calls run in. Only the WayAI backend uses it: callers never receive it.', required: true, maskInEdit: true, useExistingPlaceholder: '__USE_EXISTING_API_KEY__' },
    },
  },
  token_refresh_config: { api_key: { strategy: 'none' } },
  connector_description: 'Live voice calls on OpenAI GPT-Live: a voice that talks with callers and asks your hub\'s agent for answers. Requires an OpenAI project API key.',
};

// ---------------------------------------------------------------------------
// All connectors (sorted by connector_name for stable ordering)
// ---------------------------------------------------------------------------

// Lowercased names compared by UTF-16 code unit, not with `localeCompare`: this sort runs
// at module scope, and the first `localeCompare` in an isolate builds an ICU collator. For
// every name below the two orders agree; `connectors.test.ts` pins that.
function byLowercaseName(a: Connector, b: Connector): number {
  const x = a.connector_name.toLowerCase();
  const y = b.connector_name.toLowerCase();
  return x < y ? -1 : x > y ? 1 : 0;
}

export const CONNECTORS: Connector[] = [
  anthropic,
  apiChannel,
  claudeManagedAgents,
  elevenLabsStt,
  elevenLabsTts,
  externalResources,
  gptLive,
  googleAiStudio,
  googleTts,
  groqStt,
  groqTts,
  instagram,
  mcpServer,
  openRouter,
  openai,
  openaiStt,
  openaiTts,
  resend,
  telegram,
  restApiTool,
  rekorToolset,
  wayai,
  whatsapp,
  xai,
].sort(byLowercaseName);
